/**
 * What the help assistant shows when a turn fails to complete.
 *
 * The case worth special-casing is device setup. To reach the button's setup
 * page the caregiver joins `RemindMeVoice-Setup`, which has no internet by
 * design — so every call the app makes fails for as long as they are on it,
 * including this one. The generic "couldn't reach the assistant" then reads as
 * a broken app at the moment they are least able to judge: on 2026-07-28 a
 * council worker reported it as an outage while her broadband was fine, and
 * prod logs for that day show no errors and no request arriving at all.
 *
 * So when the request never left the phone during setup, we name the likely
 * reason instead, in the assistant's own voice. Two constraints on that text:
 *
 * - It is a **canned string**. By definition the server is unreachable when it
 *   shows, so it can never be model-written, and it must never look like an
 *   answer to whatever was actually asked — it only ever describes the
 *   assistant's own unreachability and how to get back.
 * - **Every instruction in it is conditional.** It cannot fully rule out a
 *   server fault (see `neverArrived`), and telling someone to leave the setup
 *   network when the fault is ours would walk them into the single most common
 *   cause of setup failing — the phone wandering off the setup network
 *   mid-form. So it offers both branches and tells them which is which.
 *
 * Everything that DID reach the server is kept out of that path entirely: our
 * own faults must never be reported to a caregiver as a problem with their
 * Wi-Fi.
 */

export type AssistantFailure =
  /** Render as an assistant bubble — a message from it, not a malfunction. */
  | { kind: 'notice'; content: string }
  /** Render as the red error bubble. */
  | { kind: 'error'; content: string };

/** The request never left the phone, and they are not in the setup flow. */
const UNREACHABLE_ERROR = "Sorry, I couldn't reach the assistant. Please try again.";

/** It reached us and we failed. Don't say "couldn't reach" — we were reached. */
const SERVER_FAULT = 'Sorry, something went wrong at our end. Please try again shortly.';

/** Neither our fault nor reachability, so `SERVER_FAULT` would be a false claim
 * and "try again shortly" advice that cannot work. */
const SIGN_IN_AGAIN = 'Please refresh the page and sign in again — your session may have expired.';
const SIGN_IN_CODES = new Set(['functions/unauthenticated', 'functions/permission-denied']);

const SETUP_UNREACHABLE_NOTICE = [
  "I can't reach my server at the moment, so I can't answer that just yet.",
  "If your phone is still connected to RemindMeVoice-Setup, that's the reason — that network has" +
    " no internet on purpose, so nothing in the app can load while you're on it. Switch back to" +
    ' your usual Wi-Fi and try me again.',
  "If you're already on your usual Wi-Fi, then it's a problem at our end — nothing for you to" +
    ' fix. Please try again in a few minutes.',
  'Either way, the setup steps behind this panel still work without me.',
].join('\n\n');

/**
 * Did this request fail without ever reaching the server?
 *
 * Deliberately an allowlist. A denylist of "codes that mean we were reached"
 * fails open onto the setup notice, and `assistant_chat` raises plain
 * `INTERNAL` for any Claude-side failure (`main.py`) — so an Anthropic outage
 * would tell a caregiver mid-setup to leave the setup network, which is exactly
 * the failure this module exists to prevent.
 *
 * The discriminator is the message, not the code. `postJSON` swallows a fetch
 * throw into `{status: 0, json: null}`, and `_errorForResponse` then defaults
 * the description to the code itself — so a request that never left the phone
 * arrives as `functions/internal` with the message `"internal"`, whereas a
 * server-raised `HttpsError` carries the message the function set. (Firebase
 * JS SDK 11, `@firebase/functions`.)
 *
 * Residual ambiguity, per the SDK's own comment on `case 0`: a backend that
 * dies without setting CORS headers is indistinguishable from a network
 * failure. That case is rare, and the notice's conditional phrasing stays true
 * through it.
 */
function neverArrived(error: unknown): boolean {
  const code = codeOf(error);
  if (code === undefined) return true; // not a Firebase error — nothing came back
  if (code === 'functions/deadline-exceeded') return true; // client-side timeout
  return code === 'functions/internal' && messageOf(error) === 'internal';
}

/**
 * Codes whose server message is written for the caregiver and says something
 * the generic text cannot. Both rate limits carry a wait-and-retry instruction
 * ("Daily assistant limit reached. Please try again tomorrow.") — swallowing it
 * leaves someone retrying a call that cannot succeed until midnight.
 */
const CODES_WITH_USER_FACING_MESSAGE = new Set(['functions/resource-exhausted']);

function codeOf(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('code' in error)) return undefined;
  const { code } = error as { code: unknown };
  return typeof code === 'string' ? code : undefined;
}

function messageOf(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('message' in error)) return undefined;
  const { message } = error as { message: unknown };
  return typeof message === 'string' ? message : undefined;
}

/**
 * The server's own message — but only when it is one we know is written for a
 * caregiver AND is genuinely a message. `_errorForResponse` falls back to the
 * bare code as the description whenever a response arrives without the callable
 * JSON envelope, which is exactly what a Cloud Run "no available instance" 429
 * (plain text) or a GFE quota 429 (HTML) looks like. Without this guard that
 * path renders the literal string "resource-exhausted" into a red bubble — the
 * same fallback `neverArrived` relies on, one layer up.
 */
function serverMessageFor(code: string | undefined, error: unknown): string | undefined {
  if (code === undefined || !CODES_WITH_USER_FACING_MESSAGE.has(code)) return undefined;
  const message = messageOf(error);
  return message && message !== code.slice('functions/'.length) ? message : undefined;
}

/** `context` is the widget's screen tag (e.g. `device-setup-wifi`). */
export function assistantFailureFor(error: unknown, context?: string): AssistantFailure {
  if (neverArrived(error)) {
    return context?.startsWith('device-setup')
      ? { kind: 'notice', content: SETUP_UNREACHABLE_NOTICE }
      : { kind: 'error', content: UNREACHABLE_ERROR };
  }
  const code = codeOf(error);
  if (code !== undefined && SIGN_IN_CODES.has(code)) {
    return { kind: 'error', content: SIGN_IN_AGAIN };
  }
  return { kind: 'error', content: serverMessageFor(code, error) ?? SERVER_FAULT };
}
